import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, ButtonBase, FormHelperText, MenuItem, TextField, Typography } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ParticipantFieldKind } from '@/api';
import { errorMessage, fieldErrors, imageUrl } from '../participantApi';
import { ACCENT, ACCENT_SOFT, ACCENT_TEXT, BORDER, Card, GradientButton, inputSx, MUTED, Pill, SoftButton, TextButton } from '../ui';

const valuesToMap = profile => {
  const ret = {};
  (profile.values || []).forEach(v => {
    ret[v.key] = v.value || '';
  });
  return ret;
};

const splitMulti = v => (v ? v.split(',').map(s => s.trim()).filter(Boolean) : []);

// Фото посетителя сервер принимает только PNG ≤1200px ≤4 МБ (EV-11, как и в боте, который
// конвертирует фото из Telegram). Поэтому любую картинку (jpg/png/webp, в т.ч. большое фото
// с телефона) уменьшаем до 800px по большей стороне и отправляем как PNG.
const PHOTO_MAX_SIDE = 800;
const toUploadablePhoto = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => (blob ? resolve(new File([blob], 'photo.png', { type: 'image/png' })) : reject()), 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Не удалось открыть изображение. Загрузите фото в формате JPG или PNG.'));
    };
    img.src = url;
  });

const FieldLabel = ({ field }) => (
  <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
    {field.label}
    {field.required && <Box component='span' sx={{ color: '#d14343' }}>*</Box>}
    {field.contact && <LockOutlinedIcon sx={{ fontSize: 16, color: MUTED }} />}
  </Typography>
);

const helperText = (field, error) =>
  error || (field.contact ? 'Увидят только участники, чей запрос контакта вы примете' : field.question);

const FieldInput = ({ field, value, onChange, error, onUploadPhoto, uploading }) => {
  const fileRef = useRef();
  const helper = helperText(field, error);

  if (field.kind === ParticipantFieldKind.Photo) {
    return (
      <Box>
        <FieldLabel field={field} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: '3px', borderRadius: '50%', background: BORDER }}>
            <Avatar src={imageUrl(value)} sx={{ width: 88, height: 88, border: '3px solid #fff', bgcolor: '#e9edf5' }}>
              <PhotoCameraOutlinedIcon sx={{ color: ACCENT_TEXT, fontSize: 32 }} />
            </Avatar>
          </Box>
          <input
            ref={fileRef}
            type='file'
            accept='image/png,image/jpeg,image/webp'
            hidden
            onChange={e => {
              const f = e.target.files && e.target.files[0];
              if (f) onUploadPhoto(f);
              e.target.value = '';
            }}
          />
          <SoftButton onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? 'Загружаем…' : value ? 'Заменить фото' : 'Загрузить фото'}
          </SoftButton>
        </Box>
        {helper && <FormHelperText error={!!error}>{helper}</FormHelperText>}
      </Box>
    );
  }

  if (field.kind === ParticipantFieldKind.MultiChoice && field.options && field.options.length) {
    const selected = splitMulti(value);
    const toggle = o => {
      const next = selected.includes(o) ? selected.filter(s => s !== o) : [...selected, o];
      // порядок — как в настройках поля, храним через запятую (как регформа)
      onChange(field.options.filter(x => next.includes(x)).join(','));
    };
    return (
      <Box>
        <FieldLabel field={field} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {field.options.map(o => {
            const on = selected.includes(o);
            return (
              <ButtonBase
                key={o}
                role='checkbox'
                disableRipple
                aria-checked={on}
                onClick={() => toggle(o)}
                sx={{
                  px: 1.75,
                  py: 0.85,
                  borderRadius: '999px',
                  fontSize: 14,
                  fontWeight: 600,
                  border: '1px solid',
                  borderColor: on ? 'transparent' : 'rgba(27,42,58,0.15)',
                  background: on ? ACCENT : '#fff',
                  color: on ? '#fff !important' : '#3a4a5c',
                  boxShadow: 'none',
                  transition: 'background .15s, box-shadow .15s',
                }}
              >
                {o}
              </ButtonBase>
            );
          })}
        </Box>
        {helper && <FormHelperText error={!!error}>{helper}</FormHelperText>}
      </Box>
    );
  }

  if (field.kind === ParticipantFieldKind.Choice && field.options && field.options.length) {
    return (
      <Box>
        <FieldLabel field={field} />
        <TextField select value={value} onChange={e => onChange(e.target.value)} fullWidth error={!!error}
                   helperText={helper} sx={inputSx}>
          {!field.required && <MenuItem value=''>—</MenuItem>}
          {field.options.map(o => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  }

  return (
    <Box>
      <FieldLabel field={field} />
      <TextField
        value={value}
        onChange={e => onChange(e.target.value)}
        fullWidth
        multiline={field.kind === ParticipantFieldKind.Text}
        maxRows={6}
        type={field.kind === ParticipantFieldKind.Phone ? 'tel' : 'text'}
        inputProps={{ maxLength: 512 }}
        error={!!error}
        helperText={helper}
        sx={inputSx}
      />
    </Box>
  );
};

// EV-85: анкета участника по полям, настроенным организатором (profileFields).
const ProfileEditor = ({ api, token, info, profile, onProfileChange, onPublished }) => {
  const [values, setValues] = useState(valuesToMap(profile));
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValues(valuesToMap(profile));
    setDirty(false);
  }, [profile]);

  const fields = info.fields || [];

  const change = (key, v) => {
    setValues({ ...values, [key]: v });
    setDirty(true);
    setErrors({ ...errors, [key]: undefined });
  };

  const save = () => {
    setMessage(null);
    return api
      .updateParticipantProfile(token, { values: fields.map(f => ({ key: f.key, value: values[f.key] || '' })) })
      .then(({ data }) => {
        setErrors({});
        onProfileChange(data);
        return data;
      })
      .catch(err => {
        setErrors(fieldErrors(err));
        setMessage({ severity: 'error', text: errorMessage(err) });
        throw err;
      });
  };

  const handleSave = () => {
    if (busy) return;
    setBusy(true);
    save()
      .then(() => setMessage({ severity: 'success', text: 'Анкета сохранена' }))
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const handlePublish = () => {
    if (busy) return;
    setBusy(true);
    (dirty ? save() : Promise.resolve())
      .then(() => api.publishParticipantProfile(token))
      .then(({ data }) => {
        onProfileChange(data);
        onPublished && onPublished();
      })
      .catch(err => {
        if (err && err.response) setMessage({ severity: 'error', text: errorMessage(err) });
      })
      .finally(() => setBusy(false));
  };

  const handleUnpublish = () => {
    if (busy) return;
    setBusy(true);
    api
      .unpublishParticipantProfile(token)
      .then(({ data }) => {
        onProfileChange(data);
        setMessage({ severity: 'info', text: 'Анкета скрыта: другие участники её не видят' });
      })
      .catch(err => setMessage({ severity: 'error', text: errorMessage(err) }))
      .finally(() => setBusy(false));
  };

  const uploadPhoto = (key, file) => {
    setUploading(true);
    setMessage(null);
    toUploadablePhoto(file)
      .then(photo => api.uploadParticipantPhoto(token, photo))
      .then(({ data }) => change(key, data.imageId))
      .catch(err => setErrors({ ...errors, [key]: err && err.response ? errorMessage(err) : (err && err.message) || 'Не удалось загрузить фото' }))
      .finally(() => setUploading(false));
  };

  return (
    <Card>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Моя анкета</Typography>
        {profile.published ? <Pill color='success'>Опубликована</Pill> : <Pill>Не опубликована</Pill>}
      </Box>
      <Typography sx={{ color: MUTED, mb: 3 }}>
        {profile.published
          ? 'Другие участники видят вашу анкету и могут запросить ваш контакт.'
          : 'Заполните анкету и опубликуйте её — после этого вы увидите других участников и сможете запрашивать контакты.'}
      </Typography>

      <Box sx={{ display: 'grid', gap: 2.25 }}>
        {fields.map(f => (
          <FieldInput
            key={f.key}
            field={f}
            value={values[f.key] || ''}
            onChange={v => change(f.key, v)}
            error={errors[f.key]}
            uploading={uploading}
            onUploadPhoto={file => uploadPhoto(f.key, file)}
          />
        ))}
      </Box>

      {fields.some(f => f.contact) && (
        <Box sx={{ mt: 3, p: 1.75, borderRadius: '12px', background: ACCENT_SOFT, display: 'flex', gap: 1, alignItems: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 18, color: ACCENT_TEXT }} />
          <Typography variant='body2' sx={{ color: ACCENT_TEXT }}>
            Поля с замком — ваши контакты. Их увидят только участники, чей запрос вы примете.
          </Typography>
        </Box>
      )}

      {message && (
        <Alert severity={message.severity} sx={{ mt: 3, whiteSpace: 'pre-line', borderRadius: '12px' }}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
        {profile.published ? (
          <>
            <GradientButton onClick={handleSave} disabled={busy || !dirty}>
              Сохранить
            </GradientButton>
            <TextButton onClick={handleUnpublish} disabled={busy}>
              Скрыть анкету
            </TextButton>
          </>
        ) : (
          <>
            <GradientButton onClick={handlePublish} disabled={busy}>
              Сохранить и опубликовать
            </GradientButton>
            <TextButton onClick={handleSave} disabled={busy || !dirty}>
              Сохранить черновик
            </TextButton>
          </>
        )}
      </Box>
    </Card>
  );
};

export default ProfileEditor;
